import styled, { createGlobalStyle } from "styled-components";

import { lgToneInk } from "manager/components/app-summary/AppListV2/StatusIcon";
import { COLORS } from "shared/config/constants";
import type { Vero1Severity } from "shared/interfaces/api/vero1";

import { VERO1_ACCENT, VERO1_FOCUS, VERO1_MUTED, VERO1_REQUIRED } from "./styled";

/** Violet chrome marks VERO1 authorship throughout the editor (spec §04). */
export const EditorContainer = styled.div<{ $conflict?: boolean }>`
  /* canvas 09 (360 redraw): the editor lost its 30px indent and spans the
     full row — at 360 the indent cost more than the alignment bought.
     A rejected save turns the whole card rose (§9): the conflict is the
     editor's state, not a box inside it. */
  margin: 0 0 14px;
  padding: 14px;
  border: 1px solid ${({ $conflict }) => ($conflict ? "#E26982" : VERO1_ACCENT)};
  border-radius: 4px;
  /* the bled conflict strip must clip to the card's corners */
  overflow: hidden;
  background: ${COLORS.white};
  box-shadow: 0 4px 16px -8px ${({ $conflict }) => ($conflict ? "rgba(226, 105, 130, 0.5)" : "rgba(104, 65, 186, 0.45)")};
`;

export const EditorField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 16px;
`;

/** The one dropdown skin for the review panel: antd's behavior, the canvas
 *  field's look. The platform skin was the one control left off the spec's
 *  rest/focus contract once the text fields moved to the shared canvas
 *  components. Wrap every Select in the panel — reason, row name, and the
 *  recommendation name — so one editor never shows two different fields. */
/** The menu antd renders in a portal, so it sits outside SelectField's subtree
 *  and a descendant selector cannot reach it. Options are 15px by the shared
 *  Select's own skin; the panel's field contract is 13px/300, so the two halves
 *  of one control disagreed. Pass this via dropdownClassName. */
export const VERO1_DROPDOWN_CLASS = "vero1-select-dropdown";

export const SelectFieldMenuStyles = createGlobalStyle`
  /* the shared Select's own skin sets 15px on every option via an emotion
     class; match its specificity and win on order with the dropdown class */
  .${VERO1_DROPDOWN_CLASS}.ant-select-dropdown .ant-select-dropdown-menu-item {
    font-size: 13px;
    font-weight: 300;
  }
`;

export const SelectField = styled.div`
  .ant-select {
    width: 100%;
    font: inherit;
  }

  .ant-select-selection {
    border: 1px solid ${COLORS.borders};
    border-radius: 2px;
    box-shadow: none;
  }

  .ant-select-selection:hover {
    border-color: ${COLORS.borders};
  }

  .ant-select-focused .ant-select-selection,
  .ant-select-open .ant-select-selection,
  .ant-select-selection:focus {
    border-color: ${VERO1_FOCUS};
    box-shadow: 0 0 0 3px rgba(128, 82, 208, 0.18);
  }

  .ant-select-selection--single {
    height: 38px;
  }

  .ant-select-selection__rendered {
    margin: 0 11px;
    font-size: 13px;
    font-weight: 300;
    line-height: 36px;
  }
`;

export const EditorLabel = styled.label`
  display: block;
  margin-bottom: 6px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${VERO1_MUTED};
`;

/** Dash-prefixed, italic, muted — per the spec's hint treatment. */
/** The panel's one hint treatment — every editor hint wears it, so a change
 *  here reaches all of them. The spec sets hint copy on a 1.4 line; without it
 *  these inherit the panel's body leading and read too airy for one quiet line
 *  under a control. */
/** The spec's .req: the asterisk marking a required field. One component so
 *  every label marks required the same way, rather than a literal "*" inheriting
 *  the label's muted grey. */
export const RequiredMark = styled.span`
  color: ${VERO1_REQUIRED};
`;

export const EditorHint = styled.span`
  /* inline spans take their leading from the parent's flow once they wrap, so
     a multi-line hint read taller than a single-line one; block makes the
     component's own line-height govern both */
  display: block;
  font-size: 12px;
  font-style: italic;
  font-weight: 400;
  line-height: 1.4;
  letter-spacing: 0;
  text-transform: none;
  color: ${VERO1_MUTED};
`;

/** The card opens on what Lumen said — a muted "Lumen:" beside the verdict
 *  or figure being replaced (canvas §09), not a chip. */
export const LumenReference = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 14px;
  font-size: 11.5px;
  font-weight: 400;
  color: ${COLORS.secondary};
`;

export const LumenValue = styled.span<{ $tone?: "pass" | "warn" | "fail" }>`
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  color: ${({ $tone }) => ($tone ? lgToneInk($tone) : COLORS.primary)};
`;

const SEVERITY_FILL: Record<Vero1Severity, string> = {
  pass: "#d8f3ea",
  warn: "#fbeeda",
  fail: "#fae4e9",
};

const SEVERITY_RING: Record<Vero1Severity, string> = {
  pass: "#05ce88",
  warn: "#e0a340",
  fail: "#e26982",
};

/** Selection is coloured by severity, never the violet authorship accent. */
export const SeverityToggle = styled.button<{
  $active?: boolean;
  $tone: Vero1Severity;
  $stacked?: boolean;
}>`
  flex: 1;
  min-height: 34px;
  padding: ${({ $stacked }) => ($stacked ? "6px 12px" : "4px 6px")};
  display: inline-flex;
  align-items: center;
  justify-content: ${({ $stacked }) => ($stacked ? "flex-start" : "center")};
  gap: 6px;
  border-radius: 2px;
  font-size: 12.5px;
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  cursor: pointer;
  border: 1px solid
    ${({ $active }) => ($active ? "transparent" : COLORS.borders)};
  background: ${({ $active, $tone }) =>
    $active ? SEVERITY_FILL[$tone] : COLORS.white};
  color: ${({ $active, $tone }) =>
    $active ? lgToneInk($tone) : COLORS.secondary};

  ${({ $active, $tone }) =>
    $active &&
    `outline: 2px solid ${SEVERITY_RING[$tone]};
     outline-offset: 1px;`}
`;

/** A long configured name reads poorly in a narrow share of the row, so past
 *  LONG_NAME_CHARS the toggles become full-width rows and each name gets the
 *  panel's whole width on one line. */
export const LONG_NAME_CHARS = 14;

export const SeverityToggleGroup = styled.div<{
  $attached?: boolean;
  $stacked?: boolean;
}>`
  display: flex;
  flex-direction: ${({ $stacked }) => ($stacked ? "column" : "row")};
  gap: ${({ $stacked }) => ($stacked ? "5px" : "6px")};
  margin-bottom: ${({ $attached }) => ($attached ? "0" : "5px")};
`;

const TRAY_FILL: Record<Vero1Severity, string> = {
  pass: "#f5fcf9",
  warn: "#fffaf2",
  fail: "#fef8f9",
};

const TRAY_EDGE: Record<Vero1Severity, string> = {
  pass: "#cbe9dc",
  warn: "#ffe1cc",
  fail: "#f2cbd4",
};

/** The name control is an extension of the result it names, not a second
 *  field: it butts against the toggles with no gap and shares their tone. */
export const NameTray = styled.div<{ $tone: Vero1Severity }>`
  padding: 8px 10px 10px;
  border: 1px solid ${({ $tone }) => TRAY_EDGE[$tone]};
  border-top: 0;
  border-radius: 0 0 4px 4px;
  background: ${({ $tone }) => TRAY_FILL[$tone]};
  margin-bottom: 5px;
`;

export const NameTrayLabel = styled.div<{ $tone: Vero1Severity }>`
  margin-bottom: 5px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${({ $tone }) => lgToneInk($tone)};
`;

/** A toggle that carries the client's name reads it first, with the severity
 *  word as its subtext — the same shape the deal recommendation uses. */
export const SeverityStack = styled.span<{ $inline?: boolean }>`
  display: inline-flex;
  flex-direction: ${({ $inline }) => ($inline ? "row" : "column")};
  align-items: ${({ $inline }) => ($inline ? "baseline" : "center")};
  gap: ${({ $inline }) => ($inline ? "7px" : "0")};
  line-height: 1.15;
`;

export const SeverityStackName = styled.span`
  font-size: 12.5px;
  font-weight: inherit;
`;

export const SeverityStackWord = styled.span`
  margin-top: 1px;
  font-size: 9.5px;
  font-weight: 500;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  opacity: 0.72;
`;

/** Canvas 10.1: the add affordance is a violet text link at the foot of the
 *  list, under its own VERO1 ADDITIONS heading — not an icon button. It must
 *  never reuse EditAction, which is a fixed 28px square for the row pencil. */
export const AddFieldLink = styled.button`
  align-self: flex-start;
  border: none;
  background: none;
  padding: 0;
  font-size: 12.5px;
  font-weight: 500;
  color: ${VERO1_ACCENT};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

/** Canvas 10.2 header: "NEW FIELD" in violet small-caps beside a muted
 *  "Not assessed by Lumen". */
export const NewFieldHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
`;

export const NewFieldEyebrow = styled.span`
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${VERO1_ACCENT};
`;

export const NewFieldNote = styled.span`
  font-size: 11.5px;
  font-weight: 300;
  color: ${COLORS.secondary};
`;

/** Hint under an input — 300/10.5px, tighter than the editor's field hint. */
export const FieldHint = styled.p`
  margin: 0 0 16px;
  font-size: 10.5px;
  font-weight: 300;
  line-height: 1.4;
  color: ${VERO1_MUTED};
`;

/** The scope block is separated from the label field by a rule. */
export const ScopeSection = styled.div`
  border-top: 1px solid ${COLORS.borders};
  padding-top: 14px;
  margin-bottom: 16px;
`;

export const ScopeIntro = styled.p`
  margin: 0 0 10px;
  font-size: 11px;
  font-weight: 300;
  line-height: 1.45;
  color: ${COLORS.secondary};
`;

export const ScopeList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 7px;
`;

/** Each scope is a bordered card, not a bare radio row — the selected one
 *  takes the violet border and a faint violet ground (canvas 10.2). */
/** Canvas 10.3: the company card selected turns AMBER, not violet — the
 *  scope with real blast radius states it where the choice is made, and the
 *  warning block below repeats the palette rather than introducing one. */
export const ScopeCard = styled.label<{
  $selected?: boolean;
  $warn?: boolean;
}>`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 11px;
  border-radius: 3px;
  cursor: pointer;
  border: ${({ $selected, $warn }) =>
    $selected && $warn ? "1.5px solid #b35500" : "1px solid"};
  border-color: ${({ $selected, $warn }) =>
    !$selected ? COLORS.borders : $warn ? "#b35500" : VERO1_ACCENT};
  background: ${({ $selected, $warn }) =>
    !$selected ? COLORS.white : $warn ? "#fff8f0" : "#fbfafe"};

  input {
    position: absolute;
    opacity: 0;
    pointer-events: none;
  }
`;

/** Custom 14px dot so the control matches the card, not the browser default. */
export const ScopeRadio = styled.span<{ $selected?: boolean; $warn?: boolean }>`
  flex: 0 0 auto;
  width: 14px;
  height: 14px;
  margin-top: 1px;
  border-radius: 50%;
  border: 1.5px solid
    ${({ $selected, $warn }) =>
      !$selected ? "#b7b3c9" : $warn ? "#b35500" : VERO1_ACCENT};
  display: flex;
  align-items: center;
  justify-content: center;

  &::after {
    content: "";
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: ${({ $selected, $warn }) =>
      !$selected ? "transparent" : $warn ? "#b35500" : VERO1_ACCENT};
  }
`;

export const ScopeTitle = styled.div`
  font-size: 12.5px;
  font-weight: 500;
  color: ${COLORS.primary};
`;

export const ScopeDetail = styled.div`
  margin-top: 1px;
  font-size: 11px;
  font-weight: 300;
  line-height: 1.45;
  font-variant-numeric: tabular-nums;
  color: ${COLORS.secondary};
`;

/** Canvas: the primary sits FIRST, with Cancel as a quiet text link. */
export const FormActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

export const AddFieldButton = styled.button`
  /* canvas .btnk is inline-flex, so the label never wraps; at 360 with the
     "Confirm above to continue" note beside it, a shrinkable button broke
     "Add field" across two lines */
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  height: 34px;
  padding: 0 16px;
  border: none;
  border-radius: 2px;
  font-size: 12px;
  font-weight: 500;
  color: ${COLORS.white};
  background: ${VERO1_ACCENT};
  cursor: pointer;

  &:disabled {
    background: ${COLORS.midGray};
    cursor: not-allowed;
  }
`;

export const CancelLink = styled.button`
  flex: 0 0 auto;
  white-space: nowrap;
  border: none;
  background: none;
  padding: 0;
  font-size: 12px;
  font-weight: 500;
  color: ${COLORS.secondary};
  cursor: pointer;
`;

export const FormFootnote = styled.p`
  margin: 9px 0 0;
  font-size: 10.5px;
  font-weight: 300;
  line-height: 1.4;
  color: ${VERO1_MUTED};
`;

/**
 * Canvas 10.3: company scope earns a warning block of its own — amber rule and
 * ground, not the generic rose GuardNote. Copy and checkbox stack inside it,
 * and the whole block sits between the scope list and the actions.
 */
export const CompanyWarning = styled.div`
  border-left: 2px solid #b35500;
  background: #fff8f0;
  padding: 11px 12px;
  margin-bottom: 13px;
`;

export const CompanyWarningText = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
  margin-bottom: 9px;

  svg {
    flex: 0 0 auto;
    width: 14px;
    height: 14px;
    margin-top: 1px;
  }

  p {
    margin: 0;
    font-size: 11.5px;
    font-weight: 400;
    line-height: 1.5;
    color: #7a4e00;
  }
`;

export const CompanyConfirmRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 9px;
  cursor: pointer;

  input {
    flex: 0 0 auto;
    width: 15px;
    height: 15px;
    margin: 1px 0 0;
    accent-color: #b35500;
  }

  span {
    font-size: 11.5px;
    font-weight: 500;
    line-height: 1.4;
    color: #7a4e00;
  }
`;

/** Canvas: the reason the primary is disabled sits at the row's right edge. */
export const FormActionsNote = styled.span`
  margin-left: auto;
  min-width: 0;
  font-size: 11px;
  font-weight: 300;
  color: ${COLORS.secondary};
`;
